// The part-GLB vertex source for a multi-region OGS bundle: PartRigDrive skins each
// part from one shared clip, PartBranchVertices scatters the result into branch-vertex
// order, and the branch lifts at those verts with its decoder outputs frozen.
//
// Sibling of glbVertexDriver's fused rig + association path, and the one a bundle
// shipping part GLBs takes. Per part, never a fused rig: a union skeleton gives the clip
// duplicate bone names to bind against and picks one arbitrarily.

import { loadPartRigFromGlb } from './partRigDrive.js'
import { PartBranchVertices } from './partBranchVertices.js'

/** The multi-part rig: one skinned mesh per body part over a SINGLE bone hierarchy.
 *
 *  The one file a part drive needs. The bundle owns the rig; the character's animation
 *  assets own the clips that play on it. */
export const PART_DRIVER_RIG = 'rig.glb'

/** The bundle's own part rig, or null when it ships none. */
export function partRigUrl(hasFile, base) {
  return hasFile(PART_DRIVER_RIG) ? `${base}/${PART_DRIVER_RIG}` : null
}

// A correct frame conversion lands within the bind-vs-trained-neutral pose difference,
// which is centimetres. A wrong axis or a centimetre/metre slip lands a metre out, so
// anything past this is a frame error and not a pose difference.
const MAX_NEUTRAL_RESIDUAL_METRES = 0.25

/** How well the loaded parts fit the branches they drive, per branch. */
export function partDriverFit(verts) {
  return Object.keys(verts.branches).map((name) => ({
    name,
    residual: verts.neutralResidual(name),
    disagreement: verts.maxDisagreement(name),
    // Each part's solved FBX->branch frame. The TRANSLATIONS differ per part (head 55 mm,
    // sweater 32 mm, body 22 mm), which is why one shared conversion renders the body and
    // breaks the head — see partFrameFit.js.
    parts: verts.partFits(name)?.map((fit) => ({
      name: fit.name,
      translation: [...fit.translation],
      residualMean: fit.residualMean,
      residualMax: fit.residualMax,
    })) ?? [],
  }))
}

/** `mean=12.3mm max=45.6mm spread=0.000006` — one line per branch, for the log/panel. */
export function formatPartDriverFit(branch) {
  const millimetres = (metres) => `${(metres * 1000).toFixed(1)}mm`
  const parts = (branch.parts ?? [])
    .map((part) => `${part.name.replace(/^SK_sweaterRollerSkate_|^SKM_MHC_Myra_/, '')} `
      + `+${(part.translation[1] * 1000).toFixed(0)}mm@${millimetres(part.residualMean)}`)
    .join(' ')
  return `${branch.name}: neutral mean=${millimetres(branch.residual.mean)} `
    + `max=${millimetres(branch.residual.max)} spread=${millimetres(branch.disagreement)}`
    + (parts ? `  [${parts}]` : '')
}

/** Load the multi-part rig, bind it to `branchMeshes`, and refuse a bad fit.
 *
 *  The rig is loaded for its SKELETON; what plays on it arrives afterwards, so
 *  `bodyClips` is optional and a bundle with no clip is still fully drivable. */
export async function loadPartDriver({ rigUrl, bodyClips = [], branchMeshes }) {
  const rig = await loadPartRigFromGlb(rigUrl, bodyClips)
  const verts = new PartBranchVertices(rig.meshes, branchMeshes)
  const fit = partDriverFit(verts)
  const wrong = fit.filter((branch) => branch.residual.mean > MAX_NEUTRAL_RESIDUAL_METRES)
  if (wrong.length) {
    rig.dispose()
    throw new Error('partDriver: '
      + wrong.map((branch) => `'${branch.name}' sits ${branch.residual.mean.toFixed(2)}m`).join(', ')
      + ' from the trained neutral — the parts are not in the branch frame')
  }
  return { rig, verts, fit }
}
